import { supabase } from '../lib/supabase';

// Helper to manual join user profiles since FKs might be missing in some tables
const linkProfiles = async (items, idField, targetField) => {
    if (!items || items.length === 0) return items;

    // Extract unique IDs. RPC JSON and direct table reads can differ in UUID
    // casing/whitespace, so normalize identifiers before matching them back.
    const normalizeId = value => String(value ?? '').trim().toLowerCase();
    const ids = [...new Set(items
        .map(i => normalizeId(i[idField] || i.profile_id || i.user_id || i.id))
        .filter(Boolean))];
    if (ids.length === 0) return items;

    // Fetch profiles
    // Leadership foreign keys point to user_profiles. Keep this lookup scoped
    // to columns that actually belong to that table; staff_id/pf_number and
    // job_title are legacy staff-table fields and make PostgREST reject the
    // whole query when requested from user_profiles.
    const { data: users, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, primary_role, employee_id')
        .in('id', ids);

    if (error) {
        console.error("Error fetching linked profiles:", error);
        return items;
    }

    let resolvedUsers = users || [];
    const fetchedIds = new Set(resolvedUsers.map(user => normalizeId(user.id)));
    const missingProfileIds = ids.filter(id => !fetchedIds.has(normalizeId(id)));

    // user_profiles is intentionally restricted on some deployments. The
    // scoped staff RPC is SECURITY DEFINER and already returns the profile
    // identity needed for hierarchy leadership, so use it to recover leaders
    // that the direct table policy hides from the current user.
    if (missingProfileIds.length) {
        try {
            const scopedStaff = await scopedOperationalRows('get_scoped_staff_users', 'staff', 'staff_user');
            const recovered = (scopedStaff || []).map(row => ({
                ...row,
                id: row?.id || row?.profile_id || row?.staff?.profile_id,
                full_name: row?.full_name || row?.staff?.full_name,
                email: row?.email || row?.institutional_email || row?.staff?.institutional_email,
                employee_id: row?.employee_id || row?.staff?.employee_id,
            })).filter(row => row.id && missingProfileIds.includes(normalizeId(row.id)));
            resolvedUsers = [...resolvedUsers, ...recovered];
        } catch (recoveryError) {
            console.warn('Scoped leadership profile recovery failed:', recoveryError);
        }
    }

    // Map back
    const usersById = new Map(resolvedUsers.map(user => [normalizeId(user.id), user]));
    return items.map(item => {
        const id = item[idField] || item.profile_id || item.user_id || item.id;
        const user = usersById.get(normalizeId(id));
        return {
            ...item,
            full_name: item.full_name || user?.full_name || item.name,
            email: item.email || item.institutional_email || user?.email,
            job_title: item.job_title || item.role || user?.primary_role,
            employee_id: item.employee_id || user?.employee_id,
            [targetField]: user || null
        };
    });
};

const isMissingScopeFunction = (error) =>
    error && (error.code === 'PGRST202' || /Could not find the function public\.get_scoped_/i.test(error.message || ''));

const scopedHierarchyRows = async (functionName, tableName, wrapperName, select = '*') => {
    const scopedResult = await supabase.rpc(functionName);
    if (!scopedResult.error) {
        return (scopedResult.data || []).map(row => row[wrapperName] || row);
    }
    if (!isMissingScopeFunction(scopedResult.error)) throw scopedResult.error;

    // Compatibility during deployment: existing administrators must not receive
    // an empty hierarchy merely because PostgREST has not reloaded the new RPCs.
    const fallback = await supabase.from(tableName).select(select);
    if (fallback.error) throw fallback.error;
    return fallback.data || [];
};

const scopedOperationalRows = async (functionName, tableName, wrapperName) => {
    const scopedResult = await supabase.rpc(functionName);
    if (!scopedResult.error) {
        return (scopedResult.data || []).map(row => row[wrapperName] || row);
    }
    if (!isMissingScopeFunction(scopedResult.error)) throw scopedResult.error;

    const fallback = await supabase.from(tableName).select('*');
    if (fallback.error) throw fallback.error;
    return fallback.data || [];
};

export const hierarchyService = {
    getOperationalData: async () => {
        const [programsResult, students, staffRows, applicationsResult, departments, sections] = await Promise.all([
            supabase.from('programs').select('*'),
            scopedOperationalRows('get_scoped_students', 'students', 'student'),
            scopedOperationalRows('get_scoped_staff_users', 'staff', 'staff_user'),
            supabase.from('admission_applications').select('id, program_id, status, first_name, middle_name, last_name, application_number, submitted_at, created_at'),
            scopedHierarchyRows('get_scoped_departments', 'departments', 'department'),
            scopedHierarchyRows('get_scoped_sections', 'sections', 'section')
        ]);

        const results = [programsResult, applicationsResult];
        const firstError = results.find(result => result.error)?.error;
        if (firstError) throw firstError;

        const rawStaff = (staffRows || []).map(row => {
            const userObj = row.staff_user || (row.full_name ? row : null) || row;
            const staffObj = userObj?.staff || row.staff || {};
            return {
                ...staffObj,
                ...userObj,
                full_name: userObj?.full_name || staffObj?.full_name || [userObj?.first_name, userObj?.middle_name, userObj?.last_name].filter(Boolean).join(' ') || userObj?.name,
                email: userObj?.email || staffObj?.institutional_email || userObj?.institutional_email,
                job_title: staffObj?.job_title || userObj?.job_title || userObj?.primary_role,
                employee_id: staffObj?.employee_id || userObj?.employee_id || userObj?.staff_id || userObj?.pf_number,
                department_id: userObj?.department_id || staffObj?.department_id || (userObj?.department?.id)
            };
        });
        const staff = await linkProfiles(rawStaff, 'profile_id', 'profile');

        const departmentIds = new Set(departments.map(row => row.id));
        const programs = (programsResult.data || []).filter(row => departmentIds.has(row.department_id));
        const programIds = new Set(programs.map(row => row.id));
        return {
            programs,
            students,
            staff,
            applications: (applicationsResult.data || []).filter(row => programIds.has(row.program_id)),
            departments,
            sections
        };
    },

    // --- Faculties ---
    getFaculties: async () => {
        // We try standard join first, but if it fails we might need fallback. 
        // OR we just use manual join for consistency and robustness against schema drifts.
        // Let's use manual join for Dean to be safe.

        const faculties = await scopedHierarchyRows('get_scoped_faculties', 'faculties', 'faculty');
        const linkedFaculties = await linkProfiles(faculties, 'dean_id', 'dean');

        // Some older user-management flows stored the organisational
        // assignment on user_profiles.faculty_id without also populating the
        // faculties.dean_id foreign key. Treat that relationship as a
        // backwards-compatible fallback so the hierarchy still shows the
        // assigned Dean instead of reporting "Not assigned".
        const missingFacultyIds = linkedFaculties
            .filter(faculty => !faculty?.dean && faculty?.id)
            .map(faculty => faculty.id);
        if (!missingFacultyIds.length) return linkedFaculties;

        const { data: assignedHeads, error: assignedHeadsError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, primary_role, employee_id, faculty_id, secondary_roles')
            .in('faculty_id', missingFacultyIds)
            .eq('status', 'active');

        if (assignedHeadsError) {
            // The direct dean_id relationship remains authoritative; a
            // fallback lookup must never prevent faculties from loading.
            console.warn('Faculty head fallback lookup failed:', assignedHeadsError);
            return linkedFaculties;
        }

        const isDean = profile =>
            String(profile?.primary_role || '').toLowerCase() === 'dean_of_faculty' ||
            (Array.isArray(profile?.secondary_roles) && profile.secondary_roles.some(role => String(role).toLowerCase() === 'dean_of_faculty'));

        return linkedFaculties.map(faculty => {
            if (faculty?.dean) return faculty;
            const fallbackHead = (assignedHeads || []).find(profile =>
                String(profile?.faculty_id) === String(faculty?.id) && isDean(profile),
            );
            return fallbackHead
                ? { ...faculty, dean_id: faculty.dean_id || fallbackHead.id, dean: fallbackHead }
                : faculty;
        });
    },

    createFaculty: async (facultyData) => {
        const { data, error } = await supabase
            .from('faculties')
            .insert([facultyData])
            .select()
            .single();

        if (error) throw error;
        // No need to fetch dean details on create return usually, but if UI needs it, it triggers refresh
        return data;
    },

    updateFaculty: async (id, facultyData) => {
        const { data, error } = await supabase
            .from('faculties')
            .update(facultyData)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    deleteFaculty: async (id) => {
        const { error } = await supabase
            .from('faculties')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // --- Departments ---
    getDepartments: async (facultyId = null) => {
        let departments = await scopedHierarchyRows('get_scoped_departments', 'departments', 'department', '*, faculty:faculty_id(id, name)');
        if (facultyId) departments = departments.filter(row => row.faculty_id === facultyId);
        return await linkProfiles(departments, 'hod_id', 'hod');
    },

    createDepartment: async (deptData) => {
        const { data, error } = await supabase
            .from('departments')
            .insert([deptData])
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    updateDepartment: async (id, deptData) => {
        const { data, error } = await supabase
            .from('departments')
            .update(deptData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    deleteDepartment: async (id) => {
        const { error } = await supabase
            .from('departments')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    // --- Sections ---
    getSections: async (departmentId = null) => {
        let sections = await scopedHierarchyRows('get_scoped_sections', 'sections', 'section', '*, department:department_id(id, name)');
        if (departmentId) sections = sections.filter(row => row.department_id === departmentId);
        return await linkProfiles(sections, 'hos_id', 'hos');
    },

    createSection: async (sectionData) => {
        const { data, error } = await supabase
            .from('sections')
            .insert([sectionData])
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    updateSection: async (id, sectionData) => {
        const { data, error } = await supabase
            .from('sections')
            .update(sectionData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    deleteSection: async (id) => {
        const { error } = await supabase
            .from('sections')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    getStats: async () => {
        try {
            const [faculties, departments, sections] = await Promise.all([
                scopedHierarchyRows('get_scoped_faculties', 'faculties', 'faculty'),
                scopedHierarchyRows('get_scoped_departments', 'departments', 'department'),
                scopedHierarchyRows('get_scoped_sections', 'sections', 'section')
            ]);

            return {
                faculties: faculties.length,
                departments: departments.length,
                sections: sections.length
            };
        } catch (error) {
            console.error('Error fetching hierarchy stats:', error);
            throw error;
        }
    }
};
